import React, { ReactNode } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Menu as MenuIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMobileHeaderState } from '../../context/MobileHeaderContext';
import { MOBILE_APPBAR_HEIGHT } from './mobileChrome';

// Every control in the bar is a 48x48 touch target.
const BUTTON_SX = { width: 48, height: 48, flexShrink: 0, color: 'inherit' } as const;

export interface MobileHeaderBarProps {
  /** The title when the page publishes none (the shell's page title). */
  defaultTitle: ReactNode;
  /** Opens the navigation drawer (the hamburger). */
  onOpenMenu: () => void;
}

/**
 * The phone layout's AppBar content (below md), from what the page
 * published with useMobileHeader: the back arrow (else the hamburger), a
 * one-line title (up to two lines with wrapTitle, the bar growing to fit)
 * with an optional smaller line under it, up to 2 icon actions and a
 * trailing node. No logos.
 */
export const MobileHeaderBar: React.FC<MobileHeaderBarProps> = ({ defaultTitle, onOpenMenu }) => {
  const { t } = useTranslation();
  const state = useMobileHeaderState();
  const title = state?.title ?? defaultTitle;
  const actions = (state?.actions ?? []).slice(0, 2);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: MOBILE_APPBAR_HEIGHT,
        py: state?.wrapTitle ? 0.75 : 0,
        width: '100%',
        minWidth: 0,
      }}
    >
      {state?.back ? (
        <IconButton edge="start" aria-label={state.back.label} onClick={state.back.onClick} sx={BUTTON_SX}>
          <ArrowBackIcon />
        </IconButton>
      ) : (
        <IconButton
          edge="start"
          aria-label={t('mobile.shell.openMenu', { defaultValue: 'Open menu' })}
          onClick={onOpenMenu}
          sx={BUTTON_SX}
        >
          <MenuIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0, ml: 0.5 }}>
        <Typography
          component="h1"
          noWrap={!state?.wrapTitle}
          sx={{
            fontSize: '1.0625rem',
            fontWeight: 600,
            lineHeight: 1.3,
            ...(state?.wrapTitle
              ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }
              : {}),
          }}
        >
          {title}
        </Typography>
        {state?.subtitle != null && state.subtitle !== '' && (
          <Typography
            component="div"
            variant="caption"
            noWrap
            sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.3 }}
          >
            {state.subtitle}
          </Typography>
        )}
      </Box>
      {actions.map((action) => (
        <IconButton
          key={action.key}
          aria-label={action.label}
          onClick={action.onClick}
          disabled={action.busy}
          sx={{ ...BUTTON_SX, color: action.active ? 'primary.main' : 'inherit' }}
        >
          {action.busy ? <CircularProgress size={20} color="inherit" /> : action.icon}
        </IconButton>
      ))}
      {state?.trailing}
    </Box>
  );
};

export default MobileHeaderBar;
